import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { KPIData } from '@/DTOS/KPI/KPIData';
import { KPIRecord } from '@/DTOS/KPI/KPIRecord';
import { Report } from '@/DTOS/KPI/Report';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, App, Button, Spin, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import KPIService from '../../services/kpi/KPIService';
import './KPIWeek.css';

const KPIWeek: React.FC = () => {
  const { message } = App.useApp();
  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const {
    data: reportData,
    isFetching: isReportLoading,
    isError: isErrorOnFetchReport,
    error: reportError,
  } = useQuery({
    queryKey: ['kpi'],
    queryFn: () =>
      KPIService.reportWeek().then((data: ControllerResponse<Report>) => {
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { mutate: printPdf, isPending: isPrinting } = useMutation({
    mutationFn: () => {
      if (!reportData?.id) {
        throw new Error('No report ID available');
      }
      return KPIService.printWeek(reportData.id);
    },
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'KPI_Weekly_Report.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        message.success('Report downloaded successfully');
      });
    },
    onError: (error: any) => {
      console.error('Download error:', error);
      message.error('Failed to download report: ' + (error.message || 'Unknown error'));
    },
  });

  useEffect(() => {
    setIsParamsInitialized(true);
  }, []);

  if (isReportLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchReport) {
    return <Alert message={reportError.message} type='error' />;
  }

  const convertReportToKPIData = (report: Report): KPIData[] => {
    if (!report) return [];

    const convertedRecords: KPIRecord[] = [];
    
    report.weeklyData.forEach(section => {
      section.records.forEach((record, index) => {
        convertedRecords.push({
          id: `${section.sectionName}-${index}`,
          date: report.dateRange.startDate || new Date().toISOString(),
          value: record.value,
          kpiDefineId: `${section.sectionName}-${index}`,
          kpiDefineLabel: record.label,
          kpiDefineAction: 'join', // Default action
          kpiDefineSection: section.sectionName,
          kpiDefineLabelOrder: record.labelOrder || index,
          kpiDefineIsLastDay: false,
          kpiDefineLabelTranslate: record.labelTranslate || ''
        });
      });
    });

    report.lastDayRecords.forEach(section => {
      section.records.forEach((record, index) => {
        convertedRecords.push({
          id: `${section.sectionName}-last-${index}`,
          date: report.dateRange.endDate || new Date().toISOString(),
          value: record.value,
          kpiDefineId: `${section.sectionName}-last-${index}`,
          kpiDefineLabel: record.label,
          kpiDefineAction: 'join', // Default action
          kpiDefineSection: section.sectionName,
          kpiDefineLabelOrder: record.labelOrder || index,
          kpiDefineIsLastDay: true,
          kpiDefineLabelTranslate: record.labelTranslate || ''
        });
      });
    });

    return [{
      id: 'report-data',
      date: report.dateRange.startDate || new Date().toISOString(),
      weeks: {
        currents: convertedRecords,
        summaryMonday: report.dateRange.startDate,
        summaryFriday: report.dateRange.endDate,
        trainings: ''
      }
    }];
  };

  const kpiData = reportData ? convertReportToKPIData(reportData) : [];

  const groupBySection = (records: KPIRecord[]) => {
    return records.reduce((acc: { [key: string]: KPIRecord[] }, record) => {
      if (!acc[record.kpiDefineSection]) {
        acc[record.kpiDefineSection] = [];
      }
      acc[record.kpiDefineSection].push(record);
      return acc;
    }, {});
  };

  const getLastDayItems = (records: KPIRecord[]) => {
    return records.filter((record) => record.kpiDefineIsLastDay);
  };

  const getRegularItems = (records: KPIRecord[]) => {
    return records.filter((record) => !record.kpiDefineIsLastDay);
  };

  const buildTable = (records: KPIRecord[]) => {
    const sectioned = groupBySection(getRegularItems(records));
    const sectionRows = Object.entries(sectioned).map(([section, kpis]) => {
      const labelGroups: { [key: string]: KPIRecord[] } = {};
      kpis.forEach((kpi) => {
        if (!labelGroups[kpi.kpiDefineLabel]) labelGroups[kpi.kpiDefineLabel] = [];
        labelGroups[kpi.kpiDefineLabel].push(kpi);
      });
      const sortedKPIs = Object.values(labelGroups)
        .map((group) => group[0])
        .sort((a, b) => a.kpiDefineLabelOrder - b.kpiDefineLabelOrder)
        .map((kpi) => labelGroups[kpi.kpiDefineLabel]);
      return {
        section,
        kpis: sortedKPIs,
      };
    });
    const maxKPIs = Math.max(...sectionRows.map((row) => row.kpis.length));
   
    const columns: any[] = [
      {
        title: 'Section',
        dataIndex: 'section',
        key: 'section',
        className: 'sectionCol',
        fixed: 'left',
        width: 150,
      },
    ];
    for (let i = 0; i < maxKPIs; i++) {
      columns.push(
        {
          title: `KPI ${i + 1} Label`,
          dataIndex: `kpi${i + 1}Label`,
          key: `kpi${i + 1}Label`,
          className: 'kpiLabelCol',
          render: (text: string, record: any) => {
            const kpiGroup = record[`kpi${i + 1}Group`];
            if (!kpiGroup) return text;
            return (
              <div>
                <div>{text}</div>
                <div className="kpiLabelTranslateCell">{kpiGroup[0].kpiDefineLabelTranslate}</div>
              </div>
            );
          },
        },
        {
          title: `KPI ${i + 1} Value`,
          dataIndex: `kpi${i + 1}Value`,
          key: `kpi${i + 1}Value`,
          className: 'kpiValueCol',
          render: (text: string, record: any) => {
            const kpiGroup = record[`kpi${i + 1}Group`];
            if (!kpiGroup) return '';
           
            return kpiGroup[0].value || 'No value provided';
          },
        }
      );
    }
    
    const dataSource = sectionRows.map((row) => {
      const data: any = { section: row.section };
      for (let i = 0; i < maxKPIs; i++) {
        const kpiGroup = row.kpis[i];
        data[`kpi${i + 1}Label`] = kpiGroup ? kpiGroup[0].kpiDefineLabel : '';
        data[`kpi${i + 1}Value`] = kpiGroup ? kpiGroup[0].value : '';
        data[`kpi${i + 1}Group`] = kpiGroup || null;
      }
      return data;
    });
    return { columns, dataSource };
  };

  let columns: any[] = [];
  let dataSource: any[] = [];
  if (kpiData && kpiData[0]?.weeks?.currents) {
    const table = buildTable(kpiData[0].weeks.currents);
    columns = table.columns;
    dataSource = table.dataSource;
  }

  const renderLastDayItems = () => {
    if (!kpiData?.[0]?.weeks?.currents) return null;

    const lastDayItems = getLastDayItems(kpiData[0].weeks.currents);
    if (lastDayItems.length === 0) return null;

    
    const lastDayItemsBySection = lastDayItems.reduce((acc: { [key: string]: KPIRecord[] }, item) => {
      if (!acc[item.kpiDefineSection]) {
        acc[item.kpiDefineSection] = [];
      }
      acc[item.kpiDefineSection].push(item);
      return acc;
    }, {});

    return (
      <div className="weeklySummaryContainer">
        <h3 className="weeklySummaryTitle">Weekly Summary</h3>
        {Object.entries(lastDayItemsBySection).map(([section, items], index) => (
          <div key={section}>
            {index > 0 && (
              <div className="sectionDivider" />
            )}
            <div className="sectionContainer">
              <h4 className="sectionTitle">{section}</h4>
              {items.map((item) => (
                <div key={item.id} className="kpiItemContainer">
                  <div className="kpiLabel">{item.kpiDefineLabel}</div>
                  <div className="kpiLabelTranslate">{item.kpiDefineLabelTranslate}</div>
                  <div className="kpiValue">{item.value || 'No value provided'}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderDateRange = () => {
    if (!reportData?.dateRange) return null;

    return (
      <div className="dateRangeContainer">
        <h3 className="dateRangeTitle">Report Period</h3>
        <div className="dateRangeText">
          {reportData.dateRange.formattedRange || 
           `${reportData.dateRange.startDate ? new Date(reportData.dateRange.startDate).toLocaleDateString() : 'N/A'} - ${reportData.dateRange.endDate ? new Date(reportData.dateRange.endDate).toLocaleDateString() : 'N/A'}`
          }
        </div>
      </div>
    );
  };

  return (
    <div className='kpi-week'>
      <div className='kpiTableWrap'>
        {renderDateRange()}
        <div className="buttonContainer">
          <Button type='primary' onClick={() => printPdf()} loading={isPrinting}>
            Print Weekly Report
          </Button>
        </div>
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey={(row) => row.section}
          pagination={false}
          size='middle'
          bordered
          scroll={{ x: true }}
          showHeader
          className='kpi-table'
        />
        {renderLastDayItems()}
      </div>
    </div>
  );
};

export default KPIWeek;